/** @format */

import { Action, createReducer, on } from '@ngrx/store';
import { ProductList, ProductListCore } from '@plp/data-access/interfaces';
import {
  appInitializerFeatureFlagsSet,
  availabilityFilterChanged,
  criteoProductsFetchErrored,
  criteoProductsFetchSuccessful,
  criteoProductsResolvedEmpty,
  displayableProductListBuilt,
  featureConfigCustomBadgesSet,
  filterChipsClearAllClicked,
  filterContainerClearAllClicked,
  filteringPinnedContentClicked,
  pageNumberChanged,
  pageSizeChanged,
  pinnedContentFetchErrored,
  pinnedContentFetchSuccessful,
  pinnedContentResolvedEmpty,
  plpResolverAPIDataInitialized,
  plpResolverOnLoadDataInitialized,
  plpResolverProductListFetchErrored,
  plpResolverSEOError,
  plpResolverSEONoCategories,
  productAvailabilityFetched,
  productListLoadErrored,
  productListLoadSuccessful,
  productListLoadTriggered,
  productListPromoFetched,
  pzPlpBannerFetchErrored,
  pzPlpBannerFetchSuccessful,
  pzPlpBannerResolvedEmpty,
  searchInsteadClicked,
  secondPassProductListFetched,
  selectedFilterRemoved,
  selectedFiltersUpdated,
  setInitialPageNumber,
  setInitialPageSize,
  setIsProductListPageValid,
  snbAudienceChanged,
  sortSelectionChanged,
  srlpResolverAPIDataInitialized,
  srlpResolverOnLoadDataInitialized,
  srlpResolverProductListFetchErrored,
  srlpResolverProductListFetchNoData,
} from './product-list.action';

import { DEFAULT_PAGE_SIZE } from '@plp/data-access/constants';
import { EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE } from '@shared/data-access/product-recommendations';
import { setCommonPageType } from '@state/data-access';

export const initialProductListState: ProductListCore = {
  displayableProductListData: [],
  fallbackSearchParams: {},
  featureFlags: {},
  isProductListPageLoading: true,
  isProductListPageValid: true,
  isSkeletonLoaderVisible: false,
  mlBypass: false,
  pageNumber: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  pinnedContent: null,
  pzPlpBanners: null,
  criteoProducts: null,
  productAvailability: null,
  productList: null,
  productListSEO: null,
  productPromotion: null,
  refinementUpdateObj: undefined,
  selectedFilters: {},
  selectedSort: { label: '', value: 0 },
  snbAudience: '',
  customBadges: {},
};

export const productListReducers = createReducer(
  // Supply the initial state
  initialProductListState,
  on(
    plpResolverOnLoadDataInitialized,
    srlpResolverOnLoadDataInitialized,
    (state, { data }): ProductListCore => ({
      ...state,
      ...data,
    }),
  ),
  on(plpResolverSEONoCategories, plpResolverSEOError, (state) => ({
    ...state,
    isProductListPageValid: false,
  })),
  on(
    plpResolverSEOError,
    srlpResolverProductListFetchNoData,
    srlpResolverProductListFetchErrored,
    (state) => ({
      ...state,
      isProductListPageLoading: false,
    }),
  ),
  on(srlpResolverProductListFetchErrored, (state) => ({
    ...state,
    productList: null,
  })),
  on(criteoProductsFetchSuccessful, (state, { criteoProducts }) => ({
    ...state,
    criteoProducts,
  })),
  on(criteoProductsFetchErrored, criteoProductsResolvedEmpty, (state) => ({
    ...state,
    criteoProducts: EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE,
  })),
  on(pinnedContentFetchSuccessful, (state, { pinnedContent }) => ({
    ...state,
    pinnedContent,
  })),
  on(pinnedContentFetchErrored, pinnedContentResolvedEmpty, (state) => ({
    ...state,
    pinnedContent: {},
  })),
  on(pzPlpBannerFetchSuccessful, (state, { pzPlpBanners }) => ({
    ...state,
    pzPlpBanners,
  })),
  on(pzPlpBannerFetchErrored, pzPlpBannerResolvedEmpty, (state) => ({
    ...state,
    pzPlpBanners: [],
  })),
  on(productListLoadSuccessful, (state) => ({
    ...state,
    refinementUpdateObj: undefined, // reset refinement update object on new product list load
  })),
  on(
    productListLoadSuccessful,
    plpResolverAPIDataInitialized,
    srlpResolverAPIDataInitialized,
    secondPassProductListFetched,
    plpResolverProductListFetchErrored,
    (state, props) => {
      let productList: ProductList | null;
      switch (props.type) {
        case productListLoadSuccessful.type:
        case secondPassProductListFetched.type:
          productList = props.productList;
          break;
        case plpResolverAPIDataInitialized.type:
        case srlpResolverAPIDataInitialized.type:
          productList = props.data.productList;
          break;
        case plpResolverProductListFetchErrored.type:
          productList = props.fallbackProductList;
          break;
      }

      // Scenario: If the search term has a typo or missing letter and mlSpelling is true, then didYouMeanVO will have suggestions.
      // If didYouMeanVO is present, then update the search term with the suggestion
      if (productList?.didYouMeanVO?.suggestions?.length && !state.mlBypass) {
        const dym: string = productList.didYouMeanVO.suggestions[0];
        return {
          ...state,
          productList: {
            ...productList,
            searchVO: {
              ...productList.searchVO,
              searchTerm: dym,
            },
          },
          isSkeletonLoaderVisible: false,
        };
      }

      return {
        ...state,
        productList,
        isSkeletonLoaderVisible: false,
      };
    },
  ),
  on(productListLoadErrored, (state) => ({
    ...state,
    isSkeletonLoaderVisible: false,
    // Return back to the fallback search params from the last successful action
    selectedFilters: state.fallbackSearchParams?.filters || initialProductListState.selectedFilters,
    selectedSort: state.fallbackSearchParams?.selectedSort || initialProductListState.selectedSort,
    pageSize: state.fallbackSearchParams?.pageSize || initialProductListState.pageSize,
    pageNumber: state.fallbackSearchParams?.pageNumber || initialProductListState.pageNumber,
  })),
  on(productAvailabilityFetched, (state, { productAvailability }) => ({
    ...state,
    productAvailability,
  })),
  on(productListPromoFetched, (state, { productPromotion }) => ({
    ...state,
    productPromotion,
  })),
  on(sortSelectionChanged, (state, { selectedSort }) => ({
    ...state,
    selectedSort,
    fallbackSearchParams: {
      filters: state.selectedFilters,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      selectedSort: state.selectedSort,
    },
    pageNumber: 0,
  })),
  on(pageNumberChanged, (state, { pageNumber }) => ({
    ...state,
    pageNumber,
    fallbackSearchParams: {
      filters: state.selectedFilters,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      selectedSort: state.selectedSort,
    },
  })),
  on(pageSizeChanged, (state, { pageSize }) => {
    return {
      ...state,
      pageSize,
      pageNumber: 0,
      fallbackSearchParams: {
        filters: state.selectedFilters,
        pageNumber: state.pageNumber,
        pageSize: state.pageSize,
        selectedSort: state.selectedSort,
      },
    };
  }),
  on(setIsProductListPageValid, (state, { isProductListPageValid }) => ({
    ...state,
    isProductListPageValid,
  })),
  on(setInitialPageNumber, (state, { pageNumber }) => ({
    ...state,
    pageNumber,
  })),
  on(setInitialPageSize, (state, { pageSize }) => ({
    ...state,
    pageSize,
  })),
  on(snbAudienceChanged, (state, { snbAudience }) => ({
    ...state,
    snbAudience,
  })),
  on(displayableProductListBuilt, (state, { displayableProductListData }) => {
    const searchVO = state.productList?.searchVO;
    let isProductListPageLoading = state.isProductListPageLoading;
    // If we are on a SRLP and we are not going to be redirecting to a landing page,
    // we need to set the loading state to false
    if (searchVO?.isFamilyPage === false && !searchVO?.landingPageURL) {
      isProductListPageLoading = false;
    }

    return {
      ...state,
      displayableProductListData,
      isProductListPageLoading,
    };
  }),
  on(appInitializerFeatureFlagsSet, (state, { featureFlags }) => ({
    ...state,
    featureFlags,
  })),
  on(
    selectedFiltersUpdated,
    filteringPinnedContentClicked,
    availabilityFilterChanged,
    (state, { selectedFilters, refinementUpdateObj }) => {
      const updatedSelectedFilters = { ...state.selectedFilters };

      for (const [filterKey, filterValues] of Object.entries(selectedFilters)) {
        if (Object.values(filterValues).length > 0) {
          // Adds/updates a specific filter type with new filter values.
          updatedSelectedFilters[filterKey] = filterValues;
        } else {
          // Removes a specific filter type when its value is empty.
          delete updatedSelectedFilters[filterKey];
        }
      }
      const updatedState = {
        ...state,
        selectedFilters: updatedSelectedFilters,
        pageNumber: 0,
        refinementUpdateObj,
        fallbackSearchParams: {
          filters: state.selectedFilters,
          pageNumber: state.pageNumber,
          pageSize: state.pageSize,
          selectedSort: state.selectedSort,
        },
      };

      return updatedState;
    },
  ),
  on(selectedFilterRemoved, (state, { filterType, filterLabel }) => {
    const currentFiltersWithinFilterType: string[] = state.selectedFilters?.[filterType] ?? [];
    const updatedFilters = currentFiltersWithinFilterType?.filter((value) => value !== filterLabel);
    const selectedFilters = { ...state.selectedFilters };

    if (updatedFilters.length === 0) {
      delete selectedFilters[filterType];
    } else {
      selectedFilters[filterType] = updatedFilters;
    }

    return {
      ...state,
      selectedFilters,
      pageNumber: 0,
      fallbackSearchParams: {
        filters: state.selectedFilters,
        pageNumber: state.pageNumber,
        pageSize: state.pageSize,
        selectedSort: state.selectedSort,
      },
    };
  }),
  on(filterChipsClearAllClicked, filterContainerClearAllClicked, (state) => ({
    ...state,
    selectedFilters: {},
    pageNumber: 0,
    fallbackSearchParams: {
      filters: state.selectedFilters,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      selectedSort: state.selectedSort,
    },
  })),
  on(searchInsteadClicked, (state) => ({
    ...state,
    mlBypass: true, // Bypass "did you mean" for future requests
  })),
  on(featureConfigCustomBadgesSet, (state, { customBadges }) => ({
    ...state,
    customBadges,
  })),
  on(setCommonPageType, (state, { pageType }) => {
    if (pageType !== 'plp') {
      return state;
    }
    // keep the loading spinner off on PLP, just reserved for SRLP.
    return {
      ...state,
      isProductListPageLoading: false,
    };
  }),
  on(productListLoadTriggered, (state) => ({
    ...state,
    isSkeletonLoaderVisible: true,
  })),
);

export function productListReducer(
  state: ProductListCore | undefined,
  action: Action,
): ProductListCore {
  return productListReducers(state, action);
}
